import React, { useState, useCallback, useEffect, lazy } from 'react';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';

import formatDateFns from 'src/utils/formatDateFns';

import Hint from 'src/paginas/item/componentes/hint';
import Button from 'src/componentes/button';
import ListColumn from 'src/componentes/NewList/ListColumn';
import {
  Circle,
  EllipsisVertical,
  Pencil,
  History,
  Calculator,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import StatusBadge from 'src/componentes/StatusBadge';
import { capitalizeFirstLetter } from 'src/utils/string';
import ContentManager from 'src/componentes/content-manager';
import errorHandler from 'src/utils/error-handler';
import pPipe from 'src/utils/pPipe';
import {
  getColumns,
  getFilter,
  getData,
  getPermissions,
  exportList,
} from 'src/componentes/NewList';
import { ParseError } from 'src/services/downloadFileUrl';
import LoadingContainer from 'src/componentes/loading-container';
import Toolbar from 'src/componentes/NewList/Toolbar';
import Content from 'src/componentes/pages/content';
import { COLUNAS, STATUS_AVALIACAO_RISCO } from 'src/utils/constants';
import Results from 'src/componentes/NewList/Results';
import handleError from 'src/utils/error-handler';
import http from 'src/services/httpService';
import { renderScoreRisco } from './../../components/renderScoreRisco';
import ProgressBar from 'src/componentes/progress-bar';
import Dialog from 'src/componentes/dialog';
import Confirm from 'src/componentes/message-box/confirm';
import Panel from 'src/componentes/panel';
import Badge from 'src/componentes/Badge';

import RevisoesGerenciarSearch from '../AvaliacaoPendenteListView/Search';
import SearchSchema from '../AvaliacaoPendenteListView/SearchSchema';
import { getConfiguration } from 'src/services/commonRequests';
import { formatNumber } from 'src/utils/number';
import useAuth from 'src/hooks/useAuth';
import {
  Popover,
  PopoverBody,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  UncontrolledDropdown,
} from 'reactstrap';
import css from 'src/utils/css';
import {
  obterIdPopoverTipoProbabilidade,
  obterIdPopoverTipoSeveridade,
  obterIdPopoverSeveridade,
  obterIdPopoverProbabilidade,
} from './popoverUtils';

const Historico = lazy(() => import('src/paginas/versao-alteracao'));
const RiscoAvaliacaoEditViewNew = lazy(() => import('../AvaliacaoEditViewNew'));
const LogCalculoConclusaoDialog = lazy(() =>
  import('./LogCalculoConclusaoDialog')
);
const RiscoEditView = React.lazy(() =>
  import('src/paginas/gestao-risco/risco/RiscoEditView')
);

const classes = css`
  .popoverBody {
    max-height: 600px;
    max-width: 600px;
    overflow-y: auto;
    overflow-x: auto;
  }
`;

function RiscoAvaliacaoList({
  exibirRisco,
  entityTag,
  columnsTag,
  filterTag,
  endpoint,
  parameters,
  defaultSort,
  defaultSortBy,
  selectedFilter,
  openLastFilter,
  showToolbar,
  showTitle,
  showSearch = true,
  showNew,
  funcAtualizarAnscestral,
  refreshResumo,
  habilitarCalculoDeScores,
  refreshHeaderCustom,
  habilitarLinkRisco = true,
  configuration,
  habilitarBotaoSincronizarAvaliacoes = false,
  ehAvaliacaoPendente = false,
}) {
  const [configuracao, setConfiguracao] = useState(configuration);
  const [configuracaoAvaliacao, setConfiguracaoAvaliacao] = useState({});
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(false);
  const [, setSaved] = useState(false);
  const [updatedItems] = useState([]);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [columns, setColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [filter, setFilter] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(defaultSort);
  const [sortFieldBy, setSortFieldBy] = useState(defaultSortBy);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [defaultColumns] = useState([]);
  const [tiposSeveridade, setTiposSeveridade] = useState([]);
  const [tiposProbabilidade, setTiposProbabilidade] = useState([]);
  const [comentarioClickHintId, setComentarioClickHintId] = useState(null);
  const [comentarioHintId, setComentarioHintId] = useState(null);
  const { user, terms: resources } = useAuth();
  const intl = useIntl();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);

  const handleMouseOutPopOver = useCallback((propsPopOver) => {
    if (propsPopOver?.comentarioClickHintId) return;

    setComentarioHintId(null);
  }, []);

  const popoverTipoProbabilidade = useCallback(
    (item, valorTipoProbabilidade, index = 0, propsPopOver) => {
      const idComentario = obterIdPopoverTipoProbabilidade(item, index);

      if (!valorTipoProbabilidade?.comentario) return;

      return (
        <Popover
          placement="left"
          isOpen={propsPopOver?.comentarioHintId == idComentario}
          target={idComentario}
        >
          <PopoverBody className={classes.popoverBody}>
            <span>{valorTipoProbabilidade?.comentario}</span>
          </PopoverBody>
        </Popover>
      );
    },
    []
  );

  const handleMouseOverTipoProbabilidade = useCallback(
    (item, index = 0, propsPopOver) => {
      const idComentario = obterIdPopoverTipoProbabilidade(item, index);
      setComentarioHintId(idComentario);
      setComentarioClickHintId(
        idComentario != propsPopOver?.comentarioClickHintId
          ? null
          : comentarioClickHintId
      );
    },
    [comentarioClickHintId]
  );

  const handleMouseClickTipoProbabilidade = useCallback(
    (item, index, propsPopOver) => {
      const idTipoProbabilidade = obterIdPopoverTipoProbabilidade(item, index);

      if (propsPopOver?.comentarioClickHintId == idTipoProbabilidade) {
        setComentarioClickHintId(null);
      } else setComentarioClickHintId(idTipoProbabilidade);
    },
    []
  );

  const popoverTipoSeveridade = useCallback(
    (item, valorTipoSeveridade, index = 0, propsPopOver) => {
      const idComentario = obterIdPopoverTipoSeveridade(item, index);

      if (!valorTipoSeveridade?.comentario) return;

      return (
        <Popover
          placement="left"
          isOpen={propsPopOver?.comentarioHintId == idComentario}
          target={idComentario}
        >
          <PopoverBody className={classes.popoverBody}>
            <span>{valorTipoSeveridade?.comentario}</span>
          </PopoverBody>
        </Popover>
      );
    },
    []
  );

  const handleMouseOverTipoSeveridade = useCallback(
    (item, index = 0, propsPopOver) => {
      const idComentario = obterIdPopoverTipoSeveridade(item, index);
      setComentarioHintId(idComentario);
      setComentarioClickHintId(
        idComentario != propsPopOver?.comentarioClickHintId
          ? null
          : propsPopOver?.comentarioClickHintId
      );
    },
    []
  );

  const handleMouseClickTipoSeveridade = (item, index, propsPopOver) => {
    const idTipoSeveridade = obterIdPopoverTipoSeveridade(item, index);

    if (propsPopOver?.comentarioClickHintId == idTipoSeveridade)
      setComentarioClickHintId(null);
    else setComentarioClickHintId(idTipoSeveridade);
  };

  const popoverSeveridade = (item, propsPopOver) => {
    const idComentario = obterIdPopoverSeveridade(item);
    if (!item?.descricao && !item?.severidadeComentario) return;

    return (
      <Popover
        placement="left"
        isOpen={propsPopOver?.comentarioHintId == idComentario}
        target={idComentario}
      >
        <PopoverBody className={classes.popoverBody}>
          <span>
            {item?.severidadeComentario
              ? item?.severidadeComentario
              : item?.descricao}
          </span>
        </PopoverBody>
      </Popover>
    );
  };

  const handleMouseClickSeveridade = useCallback((item, propsPopOver) => {
    const idSeveridade = obterIdPopoverSeveridade(item);

    if (propsPopOver?.comentarioClickHintId == idSeveridade) {
      setComentarioClickHintId(null);
    } else setComentarioClickHintId(idSeveridade);
  }, []);

  const handleMouseOverSeveridade = useCallback((item, propsPopOver) => {
    const idComentario = obterIdPopoverSeveridade(item);
    setComentarioHintId(idComentario);
    setComentarioClickHintId(
      idComentario != propsPopOver?.comentarioClickHintId
        ? null
        : propsPopOver?.comentarioClickHintId
    );
  }, []);

  const popoverProbabilidade = (item, propsPopOver) => {
    const idComentario = obterIdPopoverProbabilidade(item);
    if (!item?.descricao && !item?.probabilidadeComentario) return;

    //if (propsPopOver?.comentarioHintId == idComentario) alert('nice' + item?.descricao);

    return (
      <Popover
        placement="left"
        isOpen={propsPopOver?.comentarioHintId == idComentario}
        target={idComentario}
      >
        <PopoverBody className={classes.popoverBody}>
          <span>
            {item?.probabilidadeComentario
              ? item?.probabilidadeComentario
              : item?.descricao}
          </span>
        </PopoverBody>
      </Popover>
    );
  };

  const handleMouseClickProbabilidade = useCallback((item, propsPopOver) => {
    const idProbabilidade = obterIdPopoverProbabilidade(item);

    if (propsPopOver?.comentarioClickHintId == idProbabilidade) {
      setComentarioClickHintId(null);
    } else setComentarioClickHintId(idProbabilidade);
  }, []);

  const handleMouseOverProbabilidade = useCallback((item, propsPopOver) => {
    const idComentario = obterIdPopoverProbabilidade(item);
    setComentarioHintId(idComentario);
    setComentarioClickHintId(
      idComentario != propsPopOver?.comentarioClickHintId
        ? null
        : propsPopOver?.comentarioClickHintId
    );
  }, []);

  const getTiposSeveridades = async () => {
    return await http
      .post(`/TipoSeveridade/List`, {
        parameters: { buscarExcluidos: false },
        pageSize: 1000,
      })
      .then((response) => {
        if (
          response &&
          response.data &&
          response.data.list &&
          response.data.list.length > 0
        ) {
          const fields = response.data.list;
          const colunas = [];
          if (fields && fields.length > 0) {
            fields.map((tipo, index) => {
              let colunaCC = (
                <ListColumn
                  headerText={tipo?.nome}
                  valueField={`severidadeTipo${index}`}
                  sortField="Severidade.Nome"
                  popoverFunction={(item, propsPopOver) => {
                    const valorTipoSeveridade =
                      item.valoresTipoSeveridade?.find(
                        (v) => v?.tipoSeveridade?.id == tipo.id
                      );

                    return popoverTipoSeveridade(
                      item,
                      valorTipoSeveridade,
                      index,
                      propsPopOver
                    );
                  }}
                  valueFunction={(item, propsPopOver) => {
                    const valorTipoSeveridade =
                      item.valoresTipoSeveridade?.find(
                        (v) => v?.tipoSeveridade?.id == tipo.id
                      );
                    const idComentario = obterIdPopoverTipoSeveridade(
                      item,
                      index
                    );
                    return (
                      <div
                        style={{
                          cursor: valorTipoSeveridade?.comentario
                            ? 'pointer'
                            : 'default',
                        }}
                        onClick={() =>
                          valorTipoSeveridade?.comentario &&
                          handleMouseClickTipoSeveridade(
                            item,
                            index,
                            propsPopOver
                          )
                        }
                        onMouseEnter={() =>
                          handleMouseOverTipoSeveridade(
                            item,
                            index,
                            propsPopOver
                          )
                        }
                        onMouseLeave={() => handleMouseOutPopOver(propsPopOver)}
                        //id={idComentario}
                      >
                        <span
                          style={{
                            fontWeight:
                              propsPopOver?.comentarioClickHintId ==
                              idComentario
                                ? 'bold'
                                : 'normal',
                          }}
                        >
                          {valorTipoSeveridade
                            ? valorTipoSeveridade.severidade?.nome
                            : '-'}
                        </span>
                      </div>
                    );
                  }}
                />
              );
              colunas.push(colunaCC);
            });
          }

          setTiposSeveridade(colunas);
          return colunas;
        }
      });
  };

  const getTiposProbabilidades = async () => {
    return await http
      .post(`/TipoProbabilidade/List`, {
        parameters: { buscarExcluidos: false },
        pageSize: 1000,
      })
      .then((response) => {
        if (
          response &&
          response.data &&
          response.data.list &&
          response.data.list.length > 0
        ) {
          const fields = response.data.list;
          const colunas = [];
          if (fields && fields.length > 0) {
            fields.map((tipo, index) => {
              let colunaCC = (
                <ListColumn
                  headerText={tipo?.nome}
                  valueField={`probabilidadeTipo${index}`}
                  sortField="Probabilidade.Nome"
                  popoverFunction={(item, propsPopOver) => {
                    const valorTipoProbabilidade =
                      item.valoresTipoProbabilidade?.find(
                        (v) => v?.tipoProbabilidade?.id == tipo.id
                      );

                    return popoverTipoProbabilidade(
                      item,
                      valorTipoProbabilidade,
                      index,
                      propsPopOver
                    );
                  }}
                  valueFunction={(item, propsPopOver) => {
                    const valorTipoProbabilidade =
                      item.valoresTipoProbabilidade?.find(
                        (v) => v?.tipoProbabilidade?.id == tipo.id
                      );
                    const idComentario = obterIdPopoverTipoProbabilidade(
                      item,
                      index,
                      propsPopOver
                    );
                    return (
                      <div
                        style={{
                          cursor: valorTipoProbabilidade?.comentario
                            ? 'pointer'
                            : 'default',
                        }}
                        onClick={() =>
                          valorTipoProbabilidade?.comentario &&
                          handleMouseClickTipoProbabilidade(
                            item,
                            index,
                            propsPopOver
                          )
                        }
                        onMouseEnter={() =>
                          handleMouseOverTipoProbabilidade(
                            item,
                            index,
                            propsPopOver
                          )
                        }
                        onMouseLeave={() => handleMouseOutPopOver(propsPopOver)}
                        //id={idComentario}
                      >
                        <span
                          style={{
                            fontWeight:
                              propsPopOver?.comentarioClickHintId ==
                              idComentario
                                ? 'bold'
                                : 'normal',
                          }}
                        >
                          {valorTipoProbabilidade
                            ? valorTipoProbabilidade.probabilidade?.nome
                            : '-'}
                        </span>
                      </div>
                    );
                  }}
                />
              );
              colunas.push(colunaCC);
            });
          }

          setTiposProbabilidade(colunas);
          return colunas;
        }
      });
  };

  const loadColumns = useCallback(
    async (defaultColumns) => {
      try {
        const currentColumns = await getColumns(
          defaultColumns,
          entityTag,
          columnsTag
        );

        setColumns(currentColumns.columns);
        setAllColumns(currentColumns.allColumns);
      } catch (err) {
        errorHandler(err);
      }
    },
    [entityTag, columnsTag]
  );

  const loadData = useCallback(
    async (defaultColumns, filter) => {
      if (isLoadingConfiguration) return;
      try {
        if (!defaultColumns || !defaultColumns.length)
          defaultColumns = _getDefaultColumns(ehAvaliacaoPendente);

        setLoading(true);
        const newPermissions = await getPermissions(endpoint, parameters);
        setPermissions(newPermissions);

        let newData = [];

        if (newPermissions.allowView || newPermissions.allowList) {
          const colunasSeveridade =
            configuracaoAvaliacao?.avaliarSeveridadePorTipo
              ? tiposSeveridade && tiposSeveridade.length > 0
                ? tiposSeveridade
                : await getTiposSeveridades()
              : [];

          const colunasProbabilidade =
            configuracaoAvaliacao?.avaliarProbabilidadePorTipo
              ? tiposProbabilidade && tiposProbabilidade.length > 0
                ? tiposProbabilidade
                : await getTiposProbabilidades()
              : [];

          await loadColumns(
            defaultColumns.concat(colunasSeveridade, colunasProbabilidade)
          );

          let newFilter = selectedFilter;
          if (openLastFilter && filterTag)
            newFilter = await getFilter(filterTag, true);

          if (filter && filter.valor) newFilter.valor = filter.valor;

          setFilter(newFilter);
          let valueFilter = {};
          if (newFilter != null) valueFilter = newFilter.valor;

          const model = { ...valueFilter, searchText };
          newData = await getData(
            endpoint,
            model,
            sortField,
            sortFieldBy,
            pageSize,
            currentPage - 1,
            parameters
          );

          setData(newData.list);
          setCount(newData.count);
        }
        setLoading(false);
        return newData;
      } catch (err) {
        setLoading(false);
        errorHandler(err);
      }
    },
    [
      endpoint,
      parameters,
      searchText,
      pageSize,
      currentPage,
      sortField,
      sortFieldBy,
      isLoadingConfiguration,
    ]
  );

  useEffect(() => {
    if (!configuration)
      getConfiguration('ItemConfiguracaoRisco')((configuracao) =>
        setConfiguracao(configuracao)
      )();
  }, [configuration]);

  useEffect(() => {
    setIsLoadingConfiguration(true);
    http
      .post(`/riscoAvaliacao/ObterConfiguracoesAvaliacaoRisco`, {
        riscoId: parameters.riscoId,
      })
      .then((response) => {
        setConfiguracaoAvaliacao(response.data);
      })
      .finally(() => setIsLoadingConfiguration(false));
  }, [parameters.riscoId]);

  useEffect(() => {
    if (isLoadingConfiguration) return;

    loadData();
  }, [
    loadData,
    pageSize,
    currentPage,
    searchText,
    sortField,
    sortFieldBy,
    selectedFilter,
    isLoadingConfiguration,
  ]);

  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
  }, []);

  const handleColumnsChange = useCallback(async () => {
    await loadColumns(defaultColumns);
    await loadData(defaultColumns);
  }, [defaultColumns, loadColumns, loadData]);

  const handleRefresh = useCallback(
    async (filter) => {
      await loadData(defaultColumns, filter);
      funcAtualizarAnscestral && funcAtualizarAnscestral();
      refreshResumo && refreshResumo();
    },
    [defaultColumns, loadData, funcAtualizarAnscestral, refreshResumo]
  );

  const handleChangeCurrentPage = useCallback((newValue) => {
    setCurrentPage(newValue);
  }, []);

  const handleChangePageSize = useCallback((pageSize) => {
    setPageSize(pageSize);
  }, []);

  const handleSortColumn = useCallback((sort) => {
    setSortField(sort);
  }, []);

  const handleSortByColumn = useCallback((sortBy) => {
    setSortFieldBy(sortBy);
  }, []);

  const handleExport = async (fileExtension) => {
    try {
      setLoading(true);

      let valueFilter = {};
      if (filter != null) valueFilter = filter.valor;

      let newSearchModel = {
        searchText,
        ...valueFilter,
      };

      await exportList(
        newSearchModel,
        endpoint,
        sortField,
        currentPage,
        parameters,
        fileExtension
      );
      setLoading(false);
    } catch (err) {
      setLoading(false);
      errorHandler(ParseError(err));
    }
  };

  const renderData = (item) => {
    if (!item.dataAvaliacao) return null;
    return (
      <div>
        {format(new Date(item.dataAvaliacao), 'dd MMMM yyyy', {
          locale: currentLocale,
        })}
      </div>
    );
  };

  const renderDataNew = (item) => {
    return (
      <a tabIndex="-1" onClick={() => handleDataNewClick(item)}>
        {intl.formatMessage(
          { id: 'labelAcessarAvaliacoes' },
          { avaliacoes: resources.avaliacao }
        )}
      </a>
    );
  };

  const renderToleranciaRisco = (item) => {
    const { ratingToleranciaRisco } = item;
    if (!ratingToleranciaRisco) return;
    return (
      <div>
        <Badge
          pill
          className="text-white"
          style={{ background: ratingToleranciaRisco?.cor }}
        >
          {ratingToleranciaRisco?.descricao}
        </Badge>
      </div>
    );
  };

  const renderCriterios = (item) => {
    return (
      <div>
        {item.criterios &&
          item.criterios.map((itemCriterio) => (
            <div key={itemCriterio.id}>
              <span style={{ fontWeight: 'bold' }}>
                {itemCriterio.tabela}:{' '}
              </span>
              <span>{itemCriterio.descricao}</span>
            </div>
          ))}
      </div>
    );
  };

  const renderCronograma = (item) => {
    if (!item.acoesCronograma || item.acoesCronograma.length == 0) {
      return;
    }

    return (
      <div className="progress" style={{ borderRadius: 6 }}>
        {item.acoesCronograma &&
          item.acoesCronograma.map((acao, index) => (
            <ProgressBar
              key={index}
              color={acao.farolAcao.cor}
              value={acao.percentual}
            />
          ))}
      </div>
    );
  };

  const renderGrauRisco = (item) => (
    <span>
      {item.grauRisco && item.grauRisco.cor && (
        <Circle
          fill={item.grauRisco.cor}
          color={item.grauRisco.cor}
          size={22}
        />
      )}
    </span>
  );

  const handleAvaliacaoEditCallBack = () => {
    refreshHeaderCustom && refreshHeaderCustom();
    setSaved((currentSaved) => !currentSaved);
    handleRefresh();
  };

  const handleNewClick = async () => {
    try {
      if (parameters)
        await pPipe(
          criarAvaliacaoComVlresPadrao,
          handleRefresh
        )(parameters.riscoId);
    } catch (error) {
      handleError(error);
    }
  };

  async function handleDataNewClick(item) {
    try {
      if (item) {
        ContentManager.addContent(
          <RiscoAvaliacaoEditViewNew
            handleCallBack={handleAvaliacaoEditCallBack}
            item={{ id: item.id, statusId: item.farolRiscoAvaliacao?.id }}
            parameters={{ riscoId: item.risco.id, novaAvaliacao: true }}
            risco={item?.risco}
          ></RiscoAvaliacaoEditViewNew>
        );
      } else if (parameters) await handleNewClick();
    } catch (error) {
      handleError(error);
    }
  }

  const handleOpenHistorico = (item) => {
    if (!item?.id) return;
    const codigoIntegracao = item?.idIntegracao || '-';
    const nomeRisco = item?.risco?.nome || item?.nomeRisco || '-';
    const statusAvaliacao = item?.farolRiscoAvaliacao?.descricao
      ? intl.formatMessage({ id: item.farolRiscoAvaliacao.descricao })
      : '-';
    const dataAvaliacao = item?.dataAvaliacao
      ? format(new Date(item.dataAvaliacao), currentDisplay, {
          locale: currentLocale,
        })
      : '-';

    ContentManager.addContent(
      <Dialog
        title={`${intl.formatMessage({
          id: 'label.historico',
        })} - ${codigoIntegracao}`}
        width="65%"
        minHeight="60%"
        padContent
      >
        <Panel>
          <div>
            <div>
              <strong>{capitalizeFirstLetter(resources.risco)}:</strong>{' '}
              {nomeRisco}
            </div>
            <div>
              <strong>
                {intl.formatMessage(
                  { id: 'label.dataAvaliacao' },
                  { avaliacao: resources.avaliacao }
                )}
                :
              </strong>{' '}
              {dataAvaliacao}
            </div>
            <div>
              <strong>
                {intl.formatMessage({ id: 'labelStatusAvaliacao' })}:
              </strong>
              {statusAvaliacao}
            </div>
          </div>
        </Panel>
        <Historico url={'/RiscoAvaliacao/ObterHistorico'} id={item.id} />
      </Dialog>
    );
  };

  const handleOpenLogCalculoConclusao = (item) => {
    if (!item?.id) return;

    ContentManager.addContent(
      <LogCalculoConclusaoDialog avaliacaoId={item.id} />
    );
  };

  const isAvaliacaoConcluida = (item) =>
    item?.status?.id === STATUS_AVALIACAO_RISCO.CONCLUIDO ||
    item?.farolRiscoAvaliacao?.id === STATUS_AVALIACAO_RISCO.CONCLUIDO;

  const handleDeleteClickConfirm = async (item, onRefresh) => {
    if (!item?.id) return;

    try {
      setLoading(true);
      await http.post(`${endpoint}/Remove`, { id: item.id, parameters });
      onRefresh && (await onRefresh());
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (item, onRefresh) => {
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage({
          id: 'label.desejaMesmoExcluirEsseRegistro',
        })}
        handleConfirm={() => handleDeleteClickConfirm(item, onRefresh)}
      />
    );
  };

  const renderMenu = (item, onRefresh) => {
    const canEdit = item?.permissions?.allowEdit;
    const canDelete = item?.permissions?.allowDelete;

    return (
      <UncontrolledDropdown>
        <DropdownToggle tag="a" style={{ padding: 0 }}>
          <EllipsisVertical size={18} />
        </DropdownToggle>
        <DropdownMenu container="body" placement="left">
          {canEdit && (
            <DropdownItem onClick={() => handleDataNewClick(item)}>
              <Pencil className="mr-2" size={16} />
              {intl.formatMessage(
                { id: 'label.editar' },
                { editar: resources.editar }
              )}
            </DropdownItem>
          )}
          {canEdit && item?.id && (
            <DropdownItem onClick={() => handleOpenHistorico(item)}>
              <History className="mr-2" size={16} />
              {intl.formatMessage({ id: 'label.historico' })}
            </DropdownItem>
          )}
          {item?.id && isAvaliacaoConcluida(item) && (
            <DropdownItem onClick={() => handleOpenLogCalculoConclusao(item)}>
              <Calculator className="mr-2" size={16} />
              {intl.formatMessage({ id: 'label.memoriaDeCalculo' })}
            </DropdownItem>
          )}
          {canDelete && (
            <DropdownItem onClick={() => handleDeleteClick(item, onRefresh)}>
              <Trash2 className="mr-2" size={16} />
              {intl.formatMessage({ id: 'excluir' })}
            </DropdownItem>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  };

  async function criarAvaliacaoComVlresPadrao(riscoId) {
    return await http.post('/RiscoAvaliacao/Save', { parameters: { riscoId } });
  }

  const handleNomeRiscoClick = (item) => {
    ContentManager.addContent(
      <RiscoEditView
        mode="edit"
        handleClose={(saved) => {
          if (saved) handleRefresh();
        }}
        urlList="/minhas-tarefas/avaliacao-pendente"
        item={item}
      />
    );
  };

  const renderRisco = (itemFromList) => {
    const { risco } = itemFromList;
    return habilitarLinkRisco ? (
      <a tabIndex="-1" onClick={() => handleNomeRiscoClick(risco)}>
        {itemFromList.nomeRisco}
      </a>
    ) : (
      <div>{itemFromList.nomeRisco}</div>
    );
  };

  const renderImpactosIndiretos = (item) => (
    <div>
      {item.impactosIndiretos &&
        item.impactosIndiretos.map((itemImpacto) => (
          <div key={itemImpacto.id || itemImpacto.nome}>
            <Hint item={item} itemImpacto={itemImpacto} allowSave={true} />
          </div>
        ))}
    </div>
  );

  const renderSeveridade = (item, propsPopOver) => {
    const idComentario = obterIdPopoverSeveridade(item, propsPopOver);
    const descricao = item?.severidadeComentario
      ? item?.severidadeComentario
      : item?.descricao;
    return (
      <div
        style={{
          cursor: descricao ? 'pointer' : 'default',
        }}
        onClick={() =>
          descricao && handleMouseClickSeveridade(item, propsPopOver)
        }
        onMouseEnter={() => handleMouseOverSeveridade(item, propsPopOver)}
        onMouseLeave={() => handleMouseOutPopOver(propsPopOver)}
      >
        <span
          style={{
            fontWeight:
              propsPopOver?.comentarioClickHintId == idComentario
                ? 'bold'
                : 'normal',
          }}
        >
          {item.severidade ? item.severidade : '-'}
        </span>
      </div>
    );
  };

  const renderProbabilidade = (item, propsPopOver) => {
    const idComentario = obterIdPopoverProbabilidade(item, propsPopOver);
    const descricao = item?.probabilidadeComentario
      ? item?.probabilidadeComentario
      : item?.descricao;
    return (
      <div
        style={{
          cursor: descricao ? 'pointer' : 'default',
        }}
        onClick={() =>
          descricao && handleMouseClickProbabilidade(item, propsPopOver)
        }
        onMouseEnter={() => handleMouseOverProbabilidade(item, propsPopOver)}
        onMouseLeave={() => handleMouseOutPopOver(propsPopOver)}
      >
        <span
          style={{
            fontWeight:
              propsPopOver?.comentarioClickHintId == idComentario
                ? 'bold'
                : 'normal',
          }}
        >
          {item.probabilidade ? item.probabilidade : '-'}
        </span>
      </div>
    );
  };

  const handleSyncClick = async () => {
    setLoading(true);
    try {
      await http.post('/RiscoAvaliacaoPowerBI/Sync', {
        riscoId: parameters.riscoId,
      });
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
      handleRefresh();
    }
  };

  const customButtons = () => {
    if (!habilitarBotaoSincronizarAvaliacoes) return;

    return (
      <Button
        className="ml-2"
        outline
        color="primary"
        onClick={() => handleSyncClick()}
      >
        <RefreshCw size={16} />
        {intl.formatMessage(
          { id: 'sincronizarAvaliacoes' },
          { avaliacoes: resources.avaliacoes }
        )}
      </Button>
    );
  };

  const renderStatus = (item) => {
    if (!item.farolRiscoAvaliacao) return;

    const { farolRiscoAvaliacao } = item;

    return (
      <StatusBadge
        text={
          farolRiscoAvaliacao?.descricao
            ? intl.formatMessage({ id: farolRiscoAvaliacao.descricao })
            : '-'
        }
        textColor={
          farolRiscoAvaliacao?.corFundo ? farolRiscoAvaliacao?.cor : undefined
        }
        backgroundColor={
          farolRiscoAvaliacao?.corFundo || farolRiscoAvaliacao?.cor
        }
      />
    );
  };

  function _getDefaultColumns(ehAvaliacaoPendente = false) {
    let colunas = [
      <ListColumn
        key="dataAvaliacaoNovoAcesso"
        headerText={intl.formatMessage(
          { id: 'labelAcessarAvaliacoes' },
          { avaliacoes: resources.avaliacao }
        )}
        valueField="dataAvaliacaoNovoAcesso"
        valueFunction={renderDataNew}
        default
        required
        visible={true}
      />,
      <ListColumn
        key="risco"
        headerText={capitalizeFirstLetter(resources.risco)}
        valueFunction={renderRisco}
        minWidth={'150px'}
        valueField="risco"
        sortField="Risco.UltimaVersao.Nome"
        visible={exibirRisco}
        default
      />,
      <ListColumn
        key="idIntegracao"
        headerText={intl.formatMessage({ id: 'idIntegracao' })}
        valueField="idIntegracao"
      />,
      <ListColumn
        key="impactosIndiretos"
        minWidth={'150px'}
        headerText={intl.formatMessage({ id: 'label.itensDeImpactoIndireto' })}
        valueFunction={renderImpactosIndiretos}
        valueField="impactosIndiretos"
        default
      />,
      <ListColumn
        key="dataAvaliacao"
        headerText={intl.formatMessage(
          { id: 'label.dataAvaliacao' },
          { avaliacao: resources.avaliacao }
        )}
        valueField="dataAvaliacao"
        valueFunction={renderData}
        sortField="DataAvaliacao"
        default
      />,
      <ListColumn
        key="severidade"
        headerText={intl.formatMessage(
          { id: 'label.severidade' },
          { severidade: resources.severidade }
        )}
        popoverFunction={(item, propsPopOver) => {
          return popoverSeveridade(item, propsPopOver);
        }}
        onClick={(item, propsPopOver) =>
          (item?.descricao || !item?.severidadeComentario) &&
          handleMouseClickSeveridade(item, propsPopOver)
        }
        onMouseEnter={(item, propsPopOver) =>
          handleMouseOverSeveridade(item, propsPopOver)
        }
        onMouseLeave={(_item, propsPopOver) =>
          handleMouseOutPopOver(propsPopOver)
        }
        valueFunction={(item, propsPopOver) =>
          renderSeveridade(item, propsPopOver)
        }
        valueField="severidade"
        visible={!ehAvaliacaoPendente}
        sortField="Severidade.Nome"
        default
      />,
      <ListColumn
        key="probabilidade"
        headerText={resources.probabilidade}
        popoverFunction={(item, propsPopOver) => {
          return popoverProbabilidade(item, propsPopOver);
        }}
        onClick={(item, propsPopOver) =>
          (item?.descricao || !item?.probabilidadeComentario) &&
          handleMouseClickProbabilidade(item, propsPopOver)
        }
        onMouseEnter={(item, propsPopOver) =>
          handleMouseOverProbabilidade(item, propsPopOver)
        }
        onMouseLeave={(_item, propsPopOver) =>
          handleMouseOutPopOver(propsPopOver)
        }
        valueFunction={(item, propsPopOver) =>
          renderProbabilidade(item, propsPopOver)
        }
        valueField="probabilidade"
        sortField="Probabilidade.Nome"
        visible={!ehAvaliacaoPendente}
        default
      />,
      <ListColumn
        key="grauRisco"
        headerText={intl.formatMessage(
          { id: 'grauRisco' },
          { risco: resources.risco }
        )}
        valueField="grauRisco"
        visible={!ehAvaliacaoPendente}
        valueFunction={renderGrauRisco}
        default
      />,
      <ListColumn
        key="tratamento"
        headerText={intl.formatMessage({ id: 'tratamento' })}
        valueField="tratamento"
        sortField="Tratamento.Nome"
        visible={!ehAvaliacaoPendente}
        default
      />,
      <ListColumn
        key="status"
        headerText={intl.formatMessage({ id: 'labelStatusAvaliacao' })}
        valueField="status"
        valueFunction={renderStatus}
        default
      />,
      // <ListColumn
      //   headerText={capitalizeFirstLetter(
      //     intl.formatMessage(
      //       { id: 'farolAvaliacao' },
      //       { avaliacao: resources.avaliacao }
      //     )
      //   )}
      //   valueField="farolRiscoAvaliacao"
      //   valueFunction={renderFarol}
      // />,
      <ListColumn
        key="responsavel"
        headerText={intl.formatMessage({ id: 'label.responsavel' })}
        valueField="responsavel"
        sortField="Responsavel.Nome"
        default
      />,
      <ListColumn
        key="acoesCronograma"
        headerText={intl.formatMessage({ id: 'cronograma' })}
        valueField="acoesCronograma"
        valueFunction={renderCronograma}
        visible={!ehAvaliacaoPendente}
        default
      />,
      <ListColumn
        key="impactoFinanceiroResidualEstimado"
        headerText={intl.formatMessage({
          id: 'impactoFinanceiroResidualEstimado',
        })}
        valueFunction={(item) =>
          item.impactoFinanceiroResidualEstimado ? (
            <div style={{ minWidth: 100 }}>
              {configuracao?.unidadeMedidaImpactoFinanceiro?.sigla}{' '}
              {formatNumber(item.impactoFinanceiroResidualEstimado, 2)}
            </div>
          ) : (
            <>-</>
          )
        }
        valueField="impactoFinanceiroResidualEstimado"
        visible={
          !ehAvaliacaoPendente &&
          configuracao?.acompanharImpactoFinanceiroEventoRisco
        }
      />,
      <ListColumn
        key="impactoFinanceiroInerenteEstimado"
        headerText={intl.formatMessage({
          id: 'impactoFinanceiroInerenteEstimado',
        })}
        valueFunction={(item) =>
          item.impactoFinanceiroInerenteEstimado ? (
            <div style={{ minWidth: 100 }}>
              {configuracao?.unidadeMedidaImpactoFinanceiro?.sigla}{' '}
              {formatNumber(item.impactoFinanceiroInerenteEstimado, 2)}
            </div>
          ) : (
            <>-</>
          )
        }
        valueField="impactoFinanceiroInerenteEstimado"
        visible={
          !ehAvaliacaoPendente &&
          configuracao?.avaliarRiscoInerente &&
          configuracao?.acompanharImpactoFinanceiroEventoRisco
        }
      />,

      <ListColumn
        key="reducaoImpactoFinanceiroRealizado"
        headerText={`
                  ${intl.formatMessage({
                    id: 'reducaoImpactoFinanceiroRealizado',
                  })} ${
          configuracao?.unidadeMedidaImpactoFinanceiro?.sigla
            ? `(${configuracao?.unidadeMedidaImpactoFinanceiro?.sigla})`
            : ''
        }`}
        valueFunction={(item) =>
          item.reducaoImpactoFinanceiroRealizado ? (
            <div style={{ minWidth: 100 }}>
              {configuracao?.unidadeMedidaImpactoFinanceiro?.sigla}{' '}
              {formatNumber(item.reducaoImpactoFinanceiroRealizado, 2)}
            </div>
          ) : (
            <>-</>
          )
        }
        valueField="reducaoImpactoFinanceiroRealizado"
        visible={
          !ehAvaliacaoPendente &&
          configuracao?.avaliarRiscoInerente &&
          configuracao?.acompanharImpactoFinanceiroEventoRisco
        }
      />,
      <ListColumn
        key="reducaoImpactoFinanceiroPercentual"
        headerText={intl.formatMessage({
          id: 'reducaoImpactoFinanceiroPercentual',
        })}
        valueFunction={(item) =>
          item.reducaoImpactoFinanceiroPercentual ? (
            <div style={{ minWidth: 80 }}>
              {formatNumber(item.reducaoImpactoFinanceiroPercentual, 2)} %
            </div>
          ) : (
            <>-</>
          )
        }
        valueField="reducaoImpactoFinanceiroPercentual"
        visible={
          !ehAvaliacaoPendente &&
          configuracao?.avaliarRiscoInerente &&
          configuracao?.acompanharImpactoFinanceiroEventoRisco
        }
      />,
    ];
    if (habilitarCalculoDeScores)
      colunas.push(
        <ListColumn
          key="score"
          headerText={intl.formatMessage({ id: 'pontuacao' })}
          valueFunction={renderScoreRisco}
          valueField="score"
          sortField="Score"
          default
        />
      );

    if (configuracaoAvaliacao?.exibirColunaCriterios)
      colunas.push(
        <ListColumn
          key="criterios"
          headerText={intl.formatMessage(
            { id: 'labelCriteriosProbabilidade' },
            { probabilidade: resources.probabilidade }
          )}
          valueField="criterios"
          valueFunction={renderCriterios}
          default
        />
      );

    if (configuracao?.habilitarAcompanhamentoToleranciaRisco) {
      colunas.push(
        <ListColumn
          key="toleranciaRisco"
          headerText={intl.formatMessage(
            { id: 'labelToleranciaRisco' },
            { risco: resources.risco }
          )}
          valueField="toleranciaRisco"
          default
        />,
        <ListColumn
          key="ratingToleranciaRisco"
          headerText={intl.formatMessage(
            { id: 'labelToleranciaRiscoFaixas' },
            { risco: resources.risco }
          )}
          valueField="ratingToleranciaRisco"
          valueFunction={(item) => renderToleranciaRisco(item)}
          default
        />
      );
    }
    return colunas;
  }

  const content = (
    <LoadingContainer isLoading={isLoading} className="p-2">
      {showToolbar && (
        <Toolbar
          useSavedFilter
          showSearch={showSearch}
          showExport
          onExport={handleExport}
          onColumnsChange={handleColumnsChange}
          onSearchChange={handleSearchChange}
          onRefresh={handleRefresh}
          searchText={searchText}
          searchSchema={SearchSchema}
          searchComponent={<RevisoesGerenciarSearch config={configuracao} />}
          filter={filter}
          filterTag={filterTag}
          permissions={permissions}
          columnsTag={columnsTag}
          columns={columns}
          allColumns={allColumns}
          handleNewClick={handleNewClick}
          showNew={showNew}
          customButtons={customButtons}
          isLoading={isLoading}
          filterDrawer={true}
        />
      )}
      <Results
        resources={resources}
        count={count}
        columns={columns}
        data={data}
        currentPage={currentPage}
        pageSize={pageSize}
        onChangeCurrentPage={handleChangeCurrentPage}
        onChangePageSize={handleChangePageSize}
        onSortColumn={handleSortColumn}
        onSortByColumn={handleSortByColumn}
        sort={sortField}
        sortBy={sortFieldBy}
        updatedItems={updatedItems}
        handleEditClick={handleDataNewClick}
        url={'/RiscoAvaliacao'}
        onRefresh={handleRefresh}
        handleRefreshPage={handleRefresh}
        showMenu
        renderMenu={renderMenu}
        propsPopOver={{
          comentarioHintId: comentarioHintId,
          comentarioClickHintId: comentarioClickHintId,
        }}
      />
      {/* <Legenda /> */}
    </LoadingContainer>
  );

  return (
    <React.Fragment>
      {showTitle ? <Content>{content}</Content> : content}
    </React.Fragment>
  );
}

RiscoAvaliacaoList.defaultProps = {
  columnsTag: COLUNAS.AVALIACAO,
  endpoint: '/RiscoAvaliacao',
  entityTag: null,
  defaultSort: 'DataAvaliacao',
  defaultSortBy: 'desc',
  showToolbar: true,
  showTitle: true,
  openLastFilter: true,
};

export default RiscoAvaliacaoList;
